import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isOnAppHost } from '../utils/urls';
import { motivoNoIndex } from './indexable';

/**
 * Cada llamador tiene SU etiqueta (`data-condor=<clave>`) y la quita al desmontarse.
 * Si compartieran una, al navegar de una ruta inexistente a la landing la etiqueta
 * del 404 quedaría puesta (el efecto del padre no se re-ejecuta porque su valor no
 * cambió) y la landing saldría con `noindex`. Con dos etiquetas basta una para que
 * el buscador no indexe, y ninguna sobrevive a quien la puso.
 */
function useMetaRobots(noindex: boolean, clave: string) {
  useEffect(() => {
    if (!noindex) return;
    const tag = document.createElement('meta');
    tag.name = 'robots';
    tag.content = 'noindex';
    tag.dataset.condor = clave;
    document.head.appendChild(tag);
    return () => tag.remove();
  }, [noindex, clave]);
}

/**
 * `<meta name="robots" content="noindex">` según host y ruta. Montado UNA vez dentro
 * del router (App.tsx), no por página: una página nueva no puede olvidarlo.
 *
 * 🔴 Por qué en runtime y no sólo en robots.txt: `condorsecurity.co` y
 * `app.condorsecurity.co` sirven el MISMO `dist/`, así que hay un único robots.txt para
 * los dos hosts y no puede excluir la app entera sin excluir la landing. Lo que sí
 * distingue el host es el JS en el navegador, y Google lo ejecuta antes de indexar.
 */
export function NoIndex() {
  const { pathname } = useLocation();
  useMetaRobots(motivoNoIndex(pathname, isOnAppHost()) !== null, 'host-o-ruta');
  return null;
}

/**
 * Envuelve la página de la ruta `*`. El nginx contesta 200 con el index.html para
 * TODA URL, así que sin esto una URL inexistente se indexa como página válida (*soft
 * 404*). Google acepta `noindex` en la página de error de una SPA.
 *
 * Va en el `element` de `<Route path="*">` a propósito: quien decide que la ruta no
 * existe es el mismo router, no una lista de rutas copiada que se puede desincronizar.
 */
export function NoIndexEnRutaInexistente({ children }: { children: React.ReactNode }) {
  useMetaRobots(true, 'ruta-inexistente');
  return <>{children}</>;
}
